import { z } from "zod";

export const step1Schema = z.object({
  avatar: z.instanceof(File).nullable(),
  hasMarkings: z.boolean(),
  vehicleTypeUuid: z.string().min(1, "Please select a vehicle type"),
  nickname: z.string().optional(),
  capacity: z.number().min(1, "Please select passenger capacity"),
  bags: z.number().min(0, "Please select bag capacity"),
  maintenanceFee: z.number().min(0).max(100),
});

export type Step1FormData = z.infer<typeof step1Schema>;

export const step2Schema = z.object({
  accessLevel: z.enum(["RESTRICTED", "ALL"]),
  drivers: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
      avatar: z.string(),
    })
  ),
});

export type Step2FormData = z.infer<typeof step2Schema>;
export type SelectedDriver = Step2FormData["drivers"][number];

export const step3Schema = z
  .object({
    insuranceDocId: z.string().min(1, "Please upload an insurance document"),
    insuranceExpiry: z.date().nullable().optional(),
    insuranceExpiryConfirm: z.boolean(),
    insuranceAutoVerified: z.boolean(),
    accreditationDocId: z.string().nullable(),
    accreditationExpiry: z.date().nullable(),
    accreditationExpiryConfirm: z.boolean(),
    accreditationAutoVerified: z.boolean(),
  })
  .superRefine((data, ctx) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Insurance validation
    const insuranceDate = data.insuranceExpiry;
    const isInsuranceExpired = insuranceDate && insuranceDate < today;

    if (isInsuranceExpired) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Insurance document has expired. Please upload a valid document.",
        path: ["insuranceDocId"],
      });
    } else if (!data.insuranceAutoVerified && (!insuranceDate || !data.insuranceExpiryConfirm)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please confirm the insurance expiry date",
        path: ["insuranceExpiryConfirm"],
      });
    }

    // Accreditation validation
    if (data.accreditationDocId) {
      const accreditationDate = data.accreditationExpiry;
      const isAccreditationExpired = accreditationDate && accreditationDate < today;

      if (isAccreditationExpired) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Accreditation document has expired. Please upload a valid document.",
          path: ["accreditationDocId"],
        });
      } else if (!data.accreditationAutoVerified && (!accreditationDate || !data.accreditationExpiryConfirm)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please confirm the accreditation expiry date",
          path: ["accreditationExpiryConfirm"],
        });
      }
    }
  });

export type Step3FormData = z.infer<typeof step3Schema>;
